<template>
    <div>
        <ReportNavigationTabs />
        <!-- Header -->
        <div class="row">
            <div class="col-12 q-py-sm">
                <div class="page-description--report">
                    {{ currentReportDescription }}
                </div>
            </div>
        </div>
        <!-- Report fields -->
        <q-form @submit.prevent.stop="onSubmit">
            <q-banner
                v-if="showRefreshFacilityBanner"
                inline-actions
                class="text-white bg-negative row q-mb-md"
            >
                {{ $t('Failed to load facility details. Please try again.') }}
                <template v-slot:action>
                    <q-btn
                        flat
                        color="white"
                        :label="$t('Refresh')"
                        @click="updateSelectableFilters"
                    />
                </template>
            </q-banner>

            <!-- Single report row starts here -->
            <div
                class="row q-col-gutter-sm"
                :class="{ 'q-mb-sm': $q.screen.lt.md }"
            >
                <!-- Facility picker starts here -->
                <q-select
                    v-if="!isBenchmarkReport"
                    id="facility-input"
                    v-model="selectedFacility"
                    :options="facilities"
                    :label="$t('Facility')"
                    @update:model-value="
                        val => {
                            updateSelectableFilters();
                        }
                    "
                    :clearable="true"
                    :dense="true"
                    :outlined="true"
                    :emit-value="false"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="isLoading"
                    bg-color="white"
                    color="accent"
                />
                <q-select
                    v-else
                    id="facilities-input"
                    v-model="selectedFacilityGroup"
                    :options="facilityAndFacilityGroupsOptions"
                    :label="$t('Facilities')"
                    :clearable="true"
                    :multiple="true"
                    :dense="true"
                    :outlined="true"
                    :emit-value="false"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :class="{ 'q-mb-sm': $q.screen.gt.sm }"
                    :disable="isLoading"
                    bg-color="white"
                    color="accent"
                    lazy-rules
                    :rules="[
                        val =>
                            !val ||
                            validateSelectedFacilities ||
                            $t('Please select two or more facilities.')
                    ]"
                >
                    <template v-slot:option="scope">
                        <!-- If option is a separator, show a header and a separator line -->
                        <div v-if="scope.opt.separator">
                            <q-item-label header caption class="q-pb-none">
                                {{ scope.opt.label }}
                            </q-item-label>
                            <q-separator spaced />
                        </div>
                        <!-- Otherwise render a normal option -->
                        <q-item
                            v-else
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents || {}"
                        >
                            <q-item-section :id="`option-${scope.opt.label}`">
                                <div class="flex items-center justify-between">
                                    <div>
                                        {{ scope.opt.label }}
                                    </div>
                                    <q-icon
                                        v-if="scope.selected"
                                        size="20px"
                                        color="grey-5"
                                        name="fa-light fa-circle-xmark"
                                    />
                                </div>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
                <!-- Facility picker ends here -->
                <!-- Datetime picker starts here-->
                <date-time-picker
                    id="start-period-input"
                    v-model="dateTimePicker.start"
                    :dense="true"
                    :reportMode="true"
                    :label="$t('Start') + ' (' + localTimezone + ')'"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="isLoading"
                />
                <date-time-picker
                    id="end-period-input"
                    v-model="dateTimePicker.end"
                    :dense="true"
                    :reportMode="true"
                    :label="$t('End') + ' (' + localTimezone + ')'"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="isLoading"
                    :rules="[benchmarkEndPeriodValidator]"
                />
                <!-- Datetime picker ends here-->
                <q-select
                    v-if="
                        currentReportType === 'data_entry_performance' ||
                        currentReportType === 'trends'
                    "
                    id="resolution-input"
                    :label="$t('Resolution')"
                    :options="resolutionOptions"
                    v-model="selectedResolution"
                    option-label="label"
                    option-value="value"
                    :dense="true"
                    :outlined="true"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="isLoading"
                    bg-color="white"
                    color="accent"
                >
                    <!-- Custom Option Slot -->
                    <template v-slot:option="scope">
                        <q-item v-bind="scope.itemProps">
                            <q-item-section>
                                <q-item-label class="text-body1">
                                    {{ scope.opt.label }}
                                </q-item-label>
                                <q-item-label caption class="text-grey-7">
                                    {{ scope.opt.description }}
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
                <!-- Desktop Action buttons start here-->
                <!-- This button is visible only on large screens only -->
                <!-- Any changes to this button must be reflected in the other one -->
                <div v-if="$q.screen.gt.md">
                    <q-btn
                        id="generate-button"
                        :label="$t('Generate')"
                        class="ellipsis"
                        size="15px"
                        type="submit"
                        color="positive"
                        :disable="isGenerateBtnDisabled || isLoading"
                    />
                </div>
                <!-- Desktop Action buttons end-->
            </div>

            <!-- Single report row ends here -->

            <!-- Comparison report row starts here -->
            <div class="row q-col-gutter-sm q-mb-lg">
                <div v-if="canCompare" class="col-lg-2 col-md-3 col-xs-12">
                    <div class="row items-center">
                        <div class="col">
                            <q-checkbox
                                id="comparing-checkbox"
                                size="sm"
                                v-model="isComparing"
                                :disable="isLoading"
                                color="accent"
                            />
                        </div>
                        <div class="col-10 col-sm-11 col-md-10">
                            <q-select
                                v-model="selectedComparePeriod"
                                :options="comparePeriodOptions"
                                stack-label
                                :label="$t('Compare to')"
                                :outlined="true"
                                :dense="true"
                                :emit-value="false"
                                :disable="isLoading || !isComparing"
                                bg-color="white"
                                color="accent"
                            />
                        </div>
                    </div>
                </div>
                <q-select
                    v-if="showAssetSelect"
                    id="assets-select"
                    v-model="selectedAsset"
                    :options="assetSelectOptions"
                    :label="$t('Asset')"
                    :loading="isLoadingFilters"
                    :dense="true"
                    :outlined="true"
                    :emit-value="false"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="
                        isLoading || isLoadingFilters || !selectedFacility
                    "
                    bg-color="white"
                    color="accent"
                />

                <!-- Compare Datetime picker starts here-->
                <date-time-picker
                    v-if="showComparisonInputs"
                    v-model="dateTimePicker.compareStart"
                    :dense="true"
                    :reportMode="true"
                    :label="$t('Start') + ' (' + localTimezone + ')'"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="
                        isLoading || selectedComparePeriod.value === 'previous'
                    "
                />
                <date-time-picker
                    v-if="showComparisonInputs"
                    v-model="dateTimePicker.compareEnd"
                    :dense="true"
                    :reportMode="true"
                    :label="$t('End') + ' (' + localTimezone + ')'"
                    class="col-lg-2 col-md-3 col-xs-12"
                    :disable="
                        isLoading || selectedComparePeriod.value === 'previous'
                    "
                />
                <!-- Compare Datetime picker ends here-->
                <!-- Mobile Action buttons start here-->
                <!-- This button is visible on small screens only -->
                <!-- Any changes to this button must be reflected in the other one -->
                <div v-if="$q.screen.lt.lg" class="col-lg-2 col-md-1 col-xs-12">
                    <q-btn
                        id="generate-button"
                        :label="$t('Generate')"
                        :class="{ 'full-width q-mt-sm': $q.screen.lt.md }"
                        class="ellipsis"
                        size="15px"
                        type="submit"
                        color="positive"
                        :disable="isGenerateBtnDisabled || isLoading"
                    />
                </div>
                <!-- Mobile Action buttons end-->
            </div>
            <!-- Comparison report row ends here -->
        </q-form>

        <div
            class="row"
            v-if="
                showFilterPanel &&
                isSelectableOptionsReady &&
                isReportLoaded &&
                showReport
            "
        >
            <span
                class="text-subtitle2 text-secondary q-mt-sm q-mb-md col-12"
                style="font-size: 1.1rem"
            >
                {{ $t('Filters') }}
            </span>

            <!-- Filter Panel starts here -->
            <div class="row text-secondary q-mb-md filter-row">
                <filter-panel
                    v-if="!isBenchmarkReport"
                    id="assets-filter"
                    @toggle="
                        activeOptions => {
                            filteredAssets = activeOptions;
                        }
                    "
                    :label="$t('Assets')"
                    :options="filterAvailableAssetsOptions"
                    :selectedOptions="filteredAssets"
                    :icon="'fa fa-list'"
                    :searchOption="true"
                />

                <filter-panel
                    id="losses-filter"
                    v-if="currentReportType === 'losses'"
                    @toggle="
                        activeOptions => {
                            filteredLossTypes = activeOptions;
                        }
                    "
                    :label="$t('Type of Loss')"
                    :options="lossTypeFilterOptions"
                    :selectedOptions="filteredLossTypes"
                    :icon="'fa fa-exclamation-circle'"
                />

                <filter-panel
                    id="downtimes-filter"
                    v-if="currentReportType === 'losses'"
                    @toggle="
                        activeOptions => {
                            filteredDowntimes = activeOptions;
                        }
                    "
                    :label="$t('Downtimes')"
                    :options="downtimeFilterOptions"
                    :selectedOptions="filteredDowntimes"
                    :icon="'fa fa-hourglass-half'"
                />

                <filter-panel
                    id="shifts-filter"
                    v-if="
                        currentReportType === 'losses' ||
                        currentReportType === 'kpi' ||
                        currentReportType === 'quality'
                    "
                    @toggle="
                        activeOptions => {
                            filteredShifts = activeOptions;
                        }
                    "
                    :label="$t('Shifts')"
                    :options="shiftFilterOptions"
                    :selectedOptions="filteredShifts"
                    :icon="'fa fa-clock'"
                />

                <q-btn
                    id="primary-group-filter"
                    :label="$t('Primary Grouping')"
                    v-if="
                        currentReportType === 'losses' ||
                        currentReportType === 'changeover'
                    "
                    color="accent"
                    class="filter-panel text-capitalize bg-white text-weight-medium"
                    :class="
                        $q.screen.lt.sm
                            ? 'filter-panel-mobile'
                            : $q.screen.lt.md
                              ? 'filter-panel-tablet'
                              : ''
                    "
                    icon="fa fa-layer-plus"
                    outline
                >
                    <q-menu :offset="[0, 10]">
                        <q-card class="q-pa-lg">
                            <div class="q-pb-none">
                                <div
                                    class="text-subtitle1 text-black text-weight-medium"
                                >
                                    {{ $t('Primary Grouping') }}
                                </div>
                            </div>
                            <div
                                class="groupByOptions"
                                style="max-width: 170px"
                            >
                                <div v-if="currentReportType === 'losses'">
                                    <q-radio
                                        v-model="filteredGroupBy"
                                        val="causes"
                                        :label="$t('Causes')"
                                        color="accent"
                                    />
                                    <q-radio
                                        v-model="filteredGroupBy"
                                        val="categories"
                                        :label="$t('Categories')"
                                        color="accent"
                                    />
                                </div>
                                <div v-if="currentReportType === 'changeover'">
                                    <q-radio
                                        v-model="changeoverSelectedView"
                                        val="products"
                                        :label="$t('Products')"
                                        color="accent"
                                    />
                                    <q-radio
                                        v-model="changeoverSelectedView"
                                        val="properties"
                                        :label="$t('Properties')"
                                        color="accent"
                                    />
                                    <q-radio
                                        v-model="changeoverSelectedView"
                                        val="causes"
                                        :label="$t('Causes')"
                                        color="accent"
                                    />
                                </div>
                            </div>
                        </q-card>
                    </q-menu>
                </q-btn>

                <q-btn
                    v-if="
                        currentReportType === 'losses' ||
                        currentReportType === 'quality'
                    "
                    :label="$t('Secondary Grouping')"
                    color="accent"
                    class="filter-panel text-capitalize bg-white text-weight-medium"
                    icon="fa fa-layer-minus small"
                    :class="
                        $q.screen.lt.sm
                            ? 'filter-panel-mobile'
                            : $q.screen.lt.md
                              ? 'filter-panel-tablet'
                              : ''
                    "
                    outline
                >
                    <q-menu :offset="[0, 10]">
                        <q-card class="q-pa-lg">
                            <div class="q-pb-none">
                                <div
                                    class="text-subtitle1 text-black text-weight-bold"
                                >
                                    {{ $t('Secondary Grouping') }}
                                </div>
                            </div>
                            <div
                                class="groupByOptions"
                                style="max-width: 170px"
                            >
                                <div>
                                    <q-radio
                                        v-model="filteredSecondaryGroupBy"
                                        val="assets"
                                        :label="$t('Assets')"
                                        color="accent"
                                    />
                                </div>
                                <div>
                                    <q-radio
                                        v-model="filteredSecondaryGroupBy"
                                        val="products"
                                        :label="$t('Products')"
                                        color="accent"
                                    />
                                </div>
                            </div>
                        </q-card>
                    </q-menu>
                </q-btn>
            </div>

            <!-- show selected filters data -->
            <q-card
                class="q-ma-sm text-secondary no-shadow full-width q-mb-lg bg-secondary-white applied-filters-panel"
            >
                <div class="row items-center justify-between">
                    <span
                        class="col-xs-6 col-lg-2 text-subtitle2 text-body"
                        style="font-size: 1.1rem"
                    >
                        {{ $t('Applied Filters') }}
                    </span>

                    <!-- button to reset all filters to default states -->
                    <q-btn
                        @click="initFilters"
                        color="negative "
                        :label="$t('Reset All Filters')"
                        class="bg-white"
                        outline
                    />
                </div>

                <div class="q-mt-md">
                    <div class="row q-pb-sm" v-if="filteredAssets.size > 0">
                        <div
                            class="col-sm-12 col-md q-pa-sm text-weight-medium"
                        >
                            {{ this.$t('Assets') }}
                        </div>
                        <div class="col-sm-10 col-12">
                            <div class="row">
                                <div
                                    v-for="(option, index) in Array.from(
                                        appliedFilterAssets
                                    ).slice(0, showAssets)"
                                    :key="index"
                                >
                                    <div class="col-auto q-pa-xs">
                                        <q-btn
                                            @click="removeAssetFilter(option)"
                                            outline
                                            class="bg-white text-capitalize text-weight-medium"
                                            :label="assetsMap.get(option)"
                                            icon-right="fa fa-times"
                                            style="stroke-width: 1px !important"
                                            color="secondary"
                                            :size="
                                                $q.screen.lt.sm ? 'sm' : 'md'
                                            "
                                        />
                                    </div>
                                </div>
                                <q-btn
                                    v-if="
                                        appliedFilterAssets.size >
                                        MAX_ASSETS_TO_SHOW
                                    "
                                    @click="
                                        expandAssetOptions = !expandAssetOptions
                                    "
                                    class="ellipsis btn_size_sq_sm"
                                    :style="{
                                        backgroundColor: 'secondary-white',
                                        boxShadow: 'none !important'
                                    }"
                                    :icon="
                                        !expandAssetOptions
                                            ? 'fa fa-angle-up'
                                            : 'fa fa-angle-down'
                                    "
                                    :size="$q.screen.lt.sm ? 'sm' : 'md'"
                                >
                                    <q-tooltip>{{
                                        expandAssetOptions
                                            ? $t('Show More Assets')
                                            : $t('Show Fewer Assets')
                                    }}</q-tooltip>
                                </q-btn>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div
                    v-if="
                        currentReportType === 'losses' &&
                        filteredLossTypes.size > 0
                    "
                >
                    <div class="row">
                        <div
                            class="col-sm-12 col-md q-pa-sm text-weight-medium"
                        >
                            {{ this.$t('Type of Loss') }}
                        </div>
                        <div class="col-sm-10 col-12">
                            <div class="row">
                                <div
                                    v-for="(option, index) in filteredLossTypes"
                                    :key="index"
                                >
                                    <div class="q-pa-sm">
                                        <q-btn
                                            @click="
                                                removeLossTypeFilter(option)
                                            "
                                            outline
                                            class="text-capitalize bg-white"
                                            :label="option"
                                            icon-right="fa fa-times"
                                            style="stroke-width: 1px !important"
                                            color="secondary"
                                            :size="
                                                $q.screen.lt.sm ? 'sm' : 'md'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div
                    v-if="
                        currentReportType === 'losses' &&
                        filteredDowntimes.size > 0
                    "
                >
                    <div class="row">
                        <div
                            class="col-sm-12 col-md q-pa-sm text-weight-medium"
                        >
                            {{ this.$t('Downtimes') }}
                        </div>
                        <div class="col-sm-10 col-12">
                            <div class="row">
                                <div
                                    v-for="(option, index) in filteredDowntimes"
                                    :key="index"
                                >
                                    <div class="q-pa-sm">
                                        <q-btn
                                            @click="
                                                removeDowntimeFilter(option)
                                            "
                                            outline
                                            :label="option"
                                            icon-right="fa fa-times"
                                            class="text-capitalize bg-white"
                                            style="stroke-width: 1px !important"
                                            color="secondary"
                                            :size="
                                                $q.screen.lt.sm ? 'sm' : 'md'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div
                    v-if="
                        (currentReportType === 'losses' ||
                            currentReportType === 'kpi' ||
                            currentReportType === 'quality') &&
                        filteredShifts.size > 0
                    "
                >
                    <div class="row">
                        <div
                            class="col-sm-12 col-md q-pa-sm text-weight-medium"
                        >
                            {{ this.$t('Shifts') }}
                        </div>
                        <div class="col-sm-10 col-12">
                            <div class="row">
                                <div
                                    v-for="(option, index) in filteredShifts"
                                    :key="index"
                                >
                                    <div class="q-pa-sm">
                                        <q-btn
                                            @click="removeShiftFilter(option)"
                                            outline
                                            class="text-capitalize bg-white"
                                            :label="shiftIdToName[option]"
                                            icon-right="fa fa-times"
                                            style="stroke-width: 1px !important"
                                            color="secondary"
                                            :size="
                                                $q.screen.lt.sm ? 'sm' : 'md'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div
                    v-if="
                        currentReportType === 'losses' ||
                        currentReportType === 'changeover'
                    "
                >
                    <div class="row">
                        <div class="col q-pa-sm text-weight-medium">
                            {{ $t('Primary Grouping') }}
                        </div>
                        <div class="col-sm-10 col-2">
                            <div class="row">
                                <div class="q-pa-sm text-capitalize">
                                    <span v-if="currentReportType === 'losses'">
                                        {{ filteredGroupBy }}
                                    </span>
                                    <span v-else>
                                        {{ changeoverSelectedView }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div
                    class="row"
                    v-if="
                        currentReportType === 'losses' ||
                        currentReportType === 'quality'
                    "
                >
                    <div class="col q-pa-sm text-weight-medium">
                        {{ $t('Secondary Grouping') }}
                    </div>
                    <div class="col-sm-10 col-2">
                        <div class="row">
                            <div class="row">
                                <div class="q-pa-sm text-capitalize">
                                    {{ filteredSecondaryGroupBy }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <q-separator />
                </div>
            </q-card>
        </div>

        <!-- Filter Panel ends here -->
        <!-- User guide if report has not been generated yet -->
        <div
            class="q-mt-lg text-secondary"
            v-if="!isReportLoaded && !isLoading && !showReport"
        >
            <span v-if="!isBenchmarkReport">
                {{
                    $t(
                        'To generate a report, select a facility or a facility group and choose a date range. Then click on the generate button.'
                    )
                }}
            </span>
            <span v-else>
                {{
                    $t(
                        'To generate a report, select at least two facilities or a facility group and choose a date range. Then click on the generate button.'
                    )
                }}
            </span>
        </div>

        <div v-if="isLoading" class="text-center" style="height: 100vh">
            <q-spinner
                style="margin-top: 250px"
                color="primary"
                size="3em"
                :thickness="10"
            />
            <p class="q-mt-md">
                {{ `${$t('Generating report')}...` }}
            </p>
            <q-btn
                v-if="isLoading"
                @click="onCancel"
                :label="$t('Cancel')"
                class="downloadButtonBackground ellipsis"
                text-color="primary"
                color="white"
                outline
                style="margin-right: 10px; margin-bottom: 10px"
            />
        </div>

        <div v-if="showReport && isReportLoaded" class="row">
            <!-- Table column -->
            <div class="col">
                <!-- Speed losses loading indicator (non-blocking banner) -->
                <q-banner
                    v-if="
                        currentReportType === 'losses' && isLoadingSpeedLosses
                    "
                    class="bg-blue-1 text-primary q-mb-sm"
                    inline-actions
                    rounded
                    dense
                >
                    <template v-slot:avatar>
                        <q-spinner
                            color="primary"
                            size="1.2em"
                            :thickness="5"
                        />
                    </template>
                    {{ $t('Loading speed losses...') }}
                    <template v-slot:action>
                        <q-btn
                            flat
                            size="sm"
                            color="primary"
                            :label="$t('Cancel')"
                            @click="onCancelSpeedLoss"
                        />
                    </template>
                </q-banner>
                <!-- Loss Report -->
                <loss-report
                    v-if="
                        currentReportType === 'losses' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData ===
                            undefined
                    "
                    id="loss-report"
                    :records="records[this.currentReportType].currentReportData"
                    :speedLossRecords="additionalMetrics.speed_losses"
                    :selectedAssets="filteredAssets"
                    :selectedDowntimes="filteredDowntimes"
                    :selectedLosses="filteredLossTypes"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                    :facilityId="facilityId"
                    :selectedGroupBy="filteredGroupBy"
                    :selectedSecondaryGrouping="filteredSecondaryGroupBy"
                />
                <comparison-loss-report
                    v-if="
                        currentReportType === 'losses' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData !==
                            undefined
                    "
                    id="comparison-loss-report"
                    :records="records[this.currentReportType]"
                    :speedLossRecords="additionalMetrics.speed_losses"
                    :selectedAssets="filteredAssets"
                    :selectedDowntimes="filteredDowntimes"
                    :selectedLosses="filteredLossTypes"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                    :facilityId="facilityId"
                    :selectedGroupBy="filteredGroupBy"
                    :selectedSecondaryGrouping="filteredSecondaryGroupBy"
                />

                <!-- Overall KPI -->
                <performance-report
                    id="overall-kpi-report"
                    v-if="
                        currentReportType === 'kpi' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData ===
                            undefined
                    "
                    :records="records[this.currentReportType].currentReportData"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                />
                <comparison-performance-report
                    id="comparison-overall-kpi-report"
                    v-else-if="
                        currentReportType === 'kpi' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData !==
                            undefined
                    "
                    :records="records[this.currentReportType]"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                />

                <!-- Trends Report -->
                <trends-report
                    id="trends-report"
                    v-if="
                        currentReportType === 'trends' &&
                        records[this.currentReportType] !== null
                    "
                    :records="records[this.currentReportType]"
                    :facilityId="facilityId"
                    :reportTimezone="localTimezone"
                    :filteredAssets="showFilterPanel ? filteredAssets : null"
                    :assetIdToName="Object.fromEntries(assetsMap.entries())"
                />

                <!-- Data Entry Report -->
                <data-entry-report
                    v-if="
                        currentReportType === 'data_entry_performance' &&
                        records[this.currentReportType] !== null
                    "
                    :hasComparison="
                        records[this.currentReportType].comparisonReportData !==
                        undefined
                    "
                    :records="records[this.currentReportType]"
                    :facility="facilityId"
                    :selectedAssets="filteredAssets"
                    @refresh="onSubmit"
                />

                <!-- Maintenance Report -->
                <maintenance-report
                    id="maintenance-report"
                    v-if="
                        currentReportType === 'maintenance_extended' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData ===
                            undefined
                    "
                    :records="records[this.currentReportType].currentReportData"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                />
                <comparison-maintenance-report
                    id="comparison-maintenance-report"
                    v-else-if="
                        currentReportType === 'maintenance_extended' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData !==
                            undefined
                    "
                    :records="records[this.currentReportType]"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                />

                <!-- Quality Report -->
                <quality-report
                    id="quality-report"
                    v-if="
                        currentReportType === 'quality' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData ===
                            undefined
                    "
                    :records="records[this.currentReportType].currentReportData"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                    :selectedGroupBy="filteredSecondaryGroupBy"
                />
                <comparison-quality-report
                    id="comparison-quality-report"
                    v-else-if="
                        currentReportType === 'quality' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData !==
                            undefined
                    "
                    :records="records[this.currentReportType]"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                    :selectedShifts="filteredShifts"
                    :shiftIdToNames="shiftIdToName"
                    :selectedGroupBy="filteredSecondaryGroupBy"
                />

                <!-- Changeover Report -->
                <changeover-report
                    v-if="
                        currentReportType === 'changeover' &&
                        records[this.currentReportType] !== null &&
                        records[this.currentReportType].comparisonReportData ===
                            undefined
                    "
                    :records="records[this.currentReportType].currentReportData"
                    :facilityId="facilityId"
                    :selectedAssets="filteredAssets"
                    :selectedView="changeoverSelectedView"
                />

                <!-- Benchmark Report -->
                <benchmark-report
                    v-if="
                        isBenchmarkReport &&
                        records[this.currentReportType] !== null
                    "
                    :records="records[this.currentReportType].currentReportData"
                    :selectedGroups="selectedFacilityGroup"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { posthog } from '@boot/posthog';
import DateTimePicker from '@components/DateTimePicker.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import ReportNavigationTabs from '@components/ReportNavigationTabs.vue';
import { REPORT_TYPES } from '@models/reports.js';
import AsyncManager from '@utils/asyncManager.js';
import ChangeoverReport from './ChangeoverReport/ChangeoverReport.vue';
import DataEntryReport from './DataEntryReport/DataEntryReport.vue';
import FilterPanel from './FilterPanel.vue';
import ComparisonLossReport from './LossReport/ComparisonLossReport.vue';
import LossReport from './LossReport/LossReport.vue';
import ComparisonMaintenanceReport from './MaintenanceReport/ComparisonMaintenanceReport.vue';
import MaintenanceReport from './MaintenanceReport/MaintenanceReport.vue';
import ComparisonPerformanceReport from './PerformanceReport/ComparisonPerformanceReport.vue';
import PerformanceReport from './PerformanceReport/PerformanceReport.vue';
import ComparisonQualityReport from './QualityReport/ComparisonQualityReport.vue';
import QualityReport from './QualityReport/QualityReport.vue';
import TrendsReport from './TrendsReport/TrendsReport.vue';
import { mapState, mapActions } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useSettingsStore } from '@stores/settings.js';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';
import BenchmarkReport from './BenchmarkReport/BenchmarkReport.vue';
import { shallowReactive } from 'vue';
import { isFeatureEnabled } from '@utils/featureFlags.js';

// Constants
const LOSS_REPORT_MAX_MONTHS = 6;

export default {
    name: 'Report',
    components: {
        DateTimePicker,
        FilterPanel,
        LossReport,
        ComparisonLossReport,
        PerformanceReport,
        ComparisonPerformanceReport,
        DataEntryReport,
        TrendsReport,
        MaintenanceReport,
        ComparisonMaintenanceReport,
        QualityReport,
        ComparisonQualityReport,
        ChangeoverReport,
        ReportNavigationTabs,
        BenchmarkReport
    },
    mixins: [BreadcrumbsManager],
    emits: ['reportLoading'],
    async created() {
        // initialize async manager instance for main report
        this.asyncManager = new AsyncManager({});
        // initialize async manager instance for speed losses (parallel loading)
        this.speedLossAsyncManager = new AsyncManager({});

        // set default compare period
        this.selectedComparePeriod = this.comparePeriodOptions[0];

        // initialize date range
        this.initDateRange();

        this.facilities = this.getFacilities();

        // set breadcrumbs
        this.setBreadcrumbs(this.breadcrumbs);
    },
    async mounted() {
        await this.loadFacilityGroups();

        // initialize options & records
        if (this.facility) {
            this.selectedFacility = this.facilities.filter(
                facility => facility.value === this.facility
            )[0];
        } else {
            this.preselectFacilityIfSingle();
        }
        await this.toggleSelectableResolutions();
    },
    beforeUnmount() {
        this.asyncManager.cancelAsyncLoading({});
        this.speedLossAsyncManager.cancelAsyncLoading({});
        this.resetLoadingState();
    },
    watch: {
        isLoading: {
            handler() {
                this.setIsLoadingReport(this.isLoading);
            }
        },
        currentReportType: {
            handler(newVal, oldVal) {
                this.setBreadcrumbs(this.breadcrumbs);
                if (!this.showComparisonInputs) {
                    this.isComparing = false;
                }
                this.preselectFacilityIfSingle();
                // Only call toggleSelectableResolutions if report type actually changes
                if (newVal !== oldVal) {
                    this.toggleSelectableResolutions();
                }
                if (
                    this.facilityId !== null &&
                    this.currentReportType !== null &&
                    !this.showRefreshFacilityBanner &&
                    !this.isBenchmarkReport
                ) {
                    // if no records have been loaded previously, make API call regardless
                    if (!this.records[this.currentReportType]) {
                        this.onSubmit();
                    }
                    // if comparison mode is true but comparison records are not loaded, make API call
                    else if (
                        this.isComparing &&
                        !this.records[this.currentReportType]
                            .comparisonReportData
                    ) {
                        this.onSubmit();
                    }
                    // if comparison mode is false but comparison records were previously loaded, make API call to clear old comparison data
                    else if (
                        !this.isComparing &&
                        this.records[this.currentReportType]
                            .comparisonReportData
                    ) {
                        this.onSubmit();
                    }
                }
            }
        },
        // unload resolution reports when switching resolution
        selectedResolution: {
            handler() {
                switch (this.currentReportType) {
                    case 'data_entry_performance':
                        this.records['trends'] = null;
                        break;
                    case 'trends':
                        this.records['data_entry_performance'] = null;
                        break;
                }
            }
        },
        'dateTimePicker.start': {
            handler() {
                this.setComparisonDateRange();
                this.toggleSelectableResolutions();
                this.unloadUnselectedReports();
            }
        },
        'dateTimePicker.end': {
            handler() {
                this.setComparisonDateRange();
                this.toggleSelectableResolutions();
                this.unloadUnselectedReports();
            }
        },
        selectedFacility: {
            handler(val) {
                this.resetSelectedAssets();
                this.unloadUnselectedReports();
                this.resetLoadingState();
                // Update selectable filters when facility is selected
                if (val !== null && val !== undefined) {
                    this.updateSelectableFilters();
                }
            }
        },
        selectedFacilityGroup: {
            handler() {
                this.unloadUnselectedReports();
                this.resetLoadingState();
            }
        },
        selectedAsset: {
            handler() {
                this.unloadUnselectedReports();
                this.resetLoadingState();
            }
        },
        selectedComparePeriod: {
            handler() {
                this.setComparisonDateRange();
            }
        },
        records: {
            handler() {
                const report = this.records[this.currentReportType] || null;
                const assets = new Set();
                // get assets in the report
                if (report !== null) {
                    for (const asset of report.currentReportData) {
                        assets.add(asset.asset_id);
                    }
                    // get assets in the comparison report
                    if (report.comparisonReportData) {
                        for (const asset of report.comparisonReportData) {
                            assets.add(asset.asset_id);
                        }
                    }

                    // store assets in the report
                    this.assetInReport = assets;
                }
            },
            deep: true
        }
    },
    data() {
        return {
            isLoading: false,

            // Speed losses loading state
            isLoadingSpeedLosses: false,

            // top panel
            facilities: [],
            selectedFacility: null,

            // facility groups for benchmark report
            selectedFacilityGroup: null,

            DEFAULT_ALL_ASSETS_OPTION: {
                label: this.$t('All Assets'),
                value: 'all'
            },
            assetSelectOptions: [],
            selectedAsset: null,

            dateTimePicker: {
                start: null,
                end: null,
                compareStart: null,
                compareEnd: null
            },
            selectedResolution: null,
            resolutionOptions: [],

            // filter options
            isLoadingFilters: false,
            downtimeFilterOptions: [
                {
                    label: this.$t('Micro'),
                    value: 'minor'
                },
                {
                    label: this.$t('Major'),
                    value: 'major'
                },
                {
                    label: this.$t('Planned'),
                    value: 'planned'
                },
                {
                    label: this.$t('Unplanned'),
                    value: 'unplanned'
                }
            ],
            lossTypeFilterOptions: [
                {
                    label: this.$t('Availability'),
                    value: 'availability'
                },
                {
                    label: this.$t('Performance'),
                    value: 'performance'
                }
            ],
            assetFilterOptions: null,
            shiftFilterOptions: null,

            // retry text for failed API calls
            showRefreshFacilityBanner: false,

            // all assets
            assetsMap: new Map(),

            // active filters
            filteredDowntimes: new Set(),
            filteredLossTypes: new Set(),
            filteredAssets: new Set(),
            filteredShifts: new Set(),
            filteredGroupBy: 'causes',
            filteredSecondaryGroupBy: 'assets',

            // records, camel case is not used for convenience with API naming
            records: {
                losses: null,
                kpi: null,
                data_entry_performance: null,
                trends: null,
                maintenance: null,
                quality: null
            },
            // additional metrics that supplement reports
            additionalMetrics: {
                speed_losses: null
            },
            reportTitlesMap: {
                losses: this.$t('Losses'),
                kpi: this.$t('Overall KPI'),
                trends: this.$t('Trends'),
                data_entry_performance: this.$t('Data Entry Performance'),
                maintenance_extended: this.$t('Maintenance'),
                quality: this.$t('Quality'),
                changeover: this.$t('Changeovers'),
                benchmark: this.$t('Benchmark')
            },
            reportModes: Object.freeze({
                CURRENTREPORT: 'currentReport',
                COMPARISONREPORT: 'comparisonReport'
            }),
            // shift id to name
            shiftIdToName: {},

            expandAssetOptions: true,
            showPrimaryGroupBy: false,
            showSecondaryGroupBy: false,
            showReport: false,
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

            // boolean to check if report is to be compared to previous period
            isComparing: false,
            selectedComparePeriod: null,
            comparePeriodOptions: [
                {
                    label: this.$t('Previous Period'),
                    value: 'previous'
                },
                {
                    label: this.$t('Custom Period'),
                    value: 'custom'
                }
            ],

            // store the asset in the report for filtering
            assetInReport: new Set(),
            MAX_ASSETS_TO_SHOW: 4,
            reportDescriptions: {
                losses: this.$t(
                    'Identify the top causes of downtimes with Pareto analysis.'
                ),
                kpi: this.$t(
                    'Detailed production metrics for facilities and assets.'
                ),
                trends: this.$t(
                    'Discover how production is trending over time.'
                ),
                data_entry_performance: this.$t(
                    'Calculate the data entry score for each period. It should be 100% if all major downtimes have been assigned a cause.'
                ),
                maintenance_extended: this.$t(
                    'Understand Equipment Failures & Repair Speed.'
                ),
                quality: this.$t(
                    'Review quality issues and identify the most common defects.'
                ),
                changeover: this.$t(
                    'Explore changeovers to identify problematic transitions between pairs of products or product attributes.'
                ),
                benchmark: this.$t(
                    'Compare OEE and downtime across facilities or facility groups to identify gaps, benchmark efficiency, and drive improvement.'
                )
            },
            changeoverSelectedView: 'products'
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility', 'getFacilityGroups']),
        ...mapState(useFacilitiesStore, {
            getFacilitiesFromStore: 'getFacilities'
        }),
        ...mapState(useAssetsStore, [
            'getAssets',
            'getAssetGroups',
            'getAsset'
        ]),
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            getTenant: 'tenant',
            getUser: 'user'
        }),
        /**
         * Asset ID to Name object.
         * @return {Object} - Asset ID to Name object.
         */
        assetIdToName() {
            const assetIdToName = {};
            this.assetsMap.forEach((value, key) => {
                assetIdToName[key] = value;
            });
            return assetIdToName;
        },
        /**
         * If available gets the selected facility from query
         */
        facility() {
            const facility = this.$route.query.facility;
            if (facility) {
                return parseInt(facility);
            }
            return null;
        },
        showAssets() {
            if (!this.expandAssetOptions) {
                return this.filteredAssets.size;
            } else {
                return this.filteredAssets.size > this.MAX_ASSETS_TO_SHOW
                    ? this.MAX_ASSETS_TO_SHOW
                    : this.filteredAssets.size;
            }
        },
        /**
         * Get facility details.
         */
        facilityDetail() {
            return this.getFacility(this.facility);
        },
        breadcrumbs() {
            if (Number.isInteger(this.facility)) {
                var breadcrumbs = [
                    {
                        label: this.$t('Facilities'),
                        route: {
                            name: 'facility_list'
                        }
                    },
                    {
                        label: this.facilityDetail.name,
                        route: {
                            name: 'facility_dashboard',
                            params: {
                                facilityId: parseInt(this.facilityDetail.id)
                            }
                        }
                    },
                    {
                        label: this.$t('Reports'),
                        route: {
                            name: 'reports_select',
                            query: {
                                facility: parseInt(this.facilityDetail.id)
                            }
                        }
                    },
                    {
                        label: this.reportTitlesMap[this.currentReportType]
                    }
                ];
            } else {
                breadcrumbs = [
                    {
                        label: this.$t('Facilities'),
                        route: {
                            name: 'facility_list'
                        }
                    },
                    {
                        label: this.$t('Reports'),
                        route: {
                            name: 'reports_select'
                        }
                    },
                    {
                        label: this.reportTitlesMap[this.currentReportType]
                    }
                ];
            }
            return breadcrumbs;
        },
        /**
         * Get current report type.
         * @return {string} - Current report type.
         */
        currentReportType() {
            if (this.$route.name === 'maintenance') {
                return 'maintenance_extended';
            }
            if (REPORT_TYPES.includes(this.$route.name)) {
                return this.$route.name;
            }
            return null;
        },
        /**
         * Get selectable filter options based on what is present in the result.
         * @return {Function} - Callable that returns a set.
         */
        filterOptions() {
            return field => {
                let records = this.records[this.currentReportType] || [];

                // determine which filters should be selectable
                const filterOptions = new Set();
                if (records) {
                    for (const record of records) {
                        if (!filterOptions.has(record[field])) {
                            filterOptions.add(record[field]);
                        }
                    }
                }
                return filterOptions;
            };
        },
        /**
         * Get selectable facility + facility groups options.
         * @return {Array} - Array of facility + facility groups options.
         */
        facilityAndFacilityGroupsOptions() {
            // reset facility groups options
            const options = [];
            // add facility groups to options if available
            if (this.getFacilityGroups.length > 0) {
                const groupOptions = Object.values(this.getFacilityGroups).map(
                    group => ({
                        label: group.name,
                        value: group.facilities
                    })
                );
                // add facility groups separator
                options.push({
                    separator: true,
                    label: this.$t('Facility Groups')
                });
                // sort facility groups
                groupOptions.sort((a, b) => {
                    return a.label.localeCompare(b.label);
                });
                // add facility groups options
                options.push(...groupOptions);
                // add facilities separator
                options.push({
                    separator: true,
                    label: this.$t('Facilities')
                });
            }
            // add facilities options
            options.push(...this.facilities);
            return options;
        },
        /**
         * Has only one facility or facility group available.
         * @return {Boolean}
         */
        isSingleFacilityOrGroup() {
            const nFacilityOrFacilityGroup =
                this.getFacilityGroups.length + this.facilities.length;
            return nFacilityOrFacilityGroup === 1;
        },
        /**
         * Check if a report has been loaded.
         * @return {Boolean}
         */
        isReportLoaded() {
            return Boolean(this.records[this.currentReportType]);
        },
        /**
         * Get facility Id from the selected facility element.
         * @return {Number | null}
         */
        facilityId() {
            if (
                this.selectedFacility !== null &&
                this.selectedFacility !== undefined
            ) {
                return parseInt(this.selectedFacility.value);
            }
            return null;
        },
        /**
         * Get facility Ids from the selected facility group.
         * @return {Array | null}
         */
        facilitiesIdsInSelectedFacilityGroup() {
            if (
                this.selectedFacilityGroup !== null &&
                this.selectedFacilityGroup !== undefined &&
                this.selectedFacilityGroup instanceof Array
            ) {
                const facilityIds = new Set();
                for (const facility of this.selectedFacilityGroup) {
                    // if facility.value is an array of numbers, add them to the set
                    if (Array.isArray(facility.value)) {
                        facility.value.forEach(value => {
                            facilityIds.add(parseInt(value));
                        });
                    } else {
                        facilityIds.add(parseInt(facility.value));
                    }
                }
                return Array.from(facilityIds);
            }
            return null;
        },
        /**
         * Validate if the selected facility group has at least 2 facilities.
         * @return {Boolean}
         */
        validateSelectedFacilities() {
            return (
                this.facilitiesIdsInSelectedFacilityGroup !== null &&
                this.facilitiesIdsInSelectedFacilityGroup.length >= 2
            );
        },
        /**
         * Check if the generate button should be disabled.
         * @return {Boolean}
         */
        isGenerateBtnDisabled() {
            if (this.isBenchmarkReport) {
                return !this.validateSelectedFacilities;
            }
            return this.facilityId === null || this.showRefreshFacilityBanner;
        },
        /**
         * Check if all the facility details (assets, asset groups, shifts) are loaded.
         * These are necessary for the report generation API call.
         * @return {Boolean}
         */
        isSelectableOptionsReady() {
            return (
                this.assetFilterOptions !== null &&
                this.shiftFilterOptions !== null
            );
        },
        /**
         * Boolean to check if report can be compared to previous period.
         * @return {Boolean}
         */
        canCompare() {
            return (
                this.currentReportType === 'losses' ||
                this.currentReportType === 'kpi' ||
                this.currentReportType === 'data_entry_performance' ||
                this.currentReportType === 'maintenance_extended' ||
                this.currentReportType === 'quality'
            );
        },
        /**
         * Boolean to determine if we want to show a pre-generation asset select.
         * @return {Boolean}
         */
        showAssetSelect() {
            return this.currentReportType === 'trends';
        },
        /**
         * Boolean to determine if we want to show the post-generation filters
         * @return {Boolean}
         */
        showFilterPanel() {
            // if the user is a trends report and selected asset is not all assets, then no filters should be shown
            if (
                (this.currentReportType === 'trends' &&
                    this.records[this.currentReportType] &&
                    this.selectedAsset !== this.DEFAULT_ALL_ASSETS_OPTION) ||
                this.isBenchmarkReport
            ) {
                return false;
            }

            return true;
        },
        /**
         * Filter out assets not in the report.
         * This list is used on the applied filters panel.
         * @return {Set} - Filtered assets.
         */
        appliedFilterAssets() {
            if (
                this.records[this.currentReportType] !== null &&
                this.assetInReport.size > 0
            ) {
                // Filter filteredAssets based on assets in the report
                return new Set(
                    Array.from(this.filteredAssets).filter(asset =>
                        this.assetInReport.has(asset)
                    )
                );
            }
            // Return filteredAssets if no assets in the report
            return this.filteredAssets;
        },
        /**
         * Return the options to be displayed in the asset select dropdown.
         * @return {Array} - Filtered asset groups.
         */
        filterAvailableAssetsOptions() {
            if (
                this.records[this.currentReportType] !== null &&
                this.assetInReport.size > 0
            ) {
                const filteredAssetGroups = this.assetFilterOptions.reduce(
                    (acc, group) => {
                        if (group.children) {
                            const filteredChildren = group.children.filter(
                                child => this.assetInReport.has(child.value)
                            );
                            if (filteredChildren.length > 0) {
                                acc.push({
                                    ...group,
                                    children: filteredChildren
                                });
                            }
                        } else if (this.assetInReport.has(group.value)) {
                            acc.push(group);
                        }
                        return acc;
                    },
                    []
                );
                return filteredAssetGroups;
            }
            return this.assetFilterOptions;
        },
        /**
         * Retrieves the name of the current report based on the current report type.
         * @returns {string} The name of the current report.
         */
        currentReportName() {
            return this.reportTitlesMap[this.currentReportType];
        },
        /**
         * Retrieves the description of the current report based on the selected report type.
         * @returns {string} The description of the current report.
         */
        currentReportDescription() {
            return this.reportDescriptions[this.currentReportType];
        },
        /**
         * Determines whether to show comparison inputs based on the current report type and state.
         * @returns {boolean} - Returns true if comparison inputs should be shown, otherwise false.
         */
        showComparisonInputs() {
            const isChangeoverReport = this.currentReportType === 'changeover';
            return (
                this.isComparing &&
                !isChangeoverReport &&
                !this.isBenchmarkReport &&
                !this.showAssetSelect
            );
        },
        /**
         * Check if the current report type is benchmark
         * @returns {boolean} - Returns true if the current report type is benchmark, otherwise false.
         */
        isBenchmarkReport() {
            return this.currentReportType === 'benchmark';
        },
        /**
         * Creates standardized resolution option objects with translated labels.
         * @returns {Object} Object containing all resolution types
         */
        createResolutionOptions() {
            const baseOptions = {
                hourly: {
                    label: this.$t('Hourly'),
                    value: 'hourly',
                    description: this.$t(
                        'Standard hours from 00:00 to 01:00, 01:00 to 02:00, etc.'
                    )
                },
                daily: {
                    label: this.$t('Daily'),
                    value: 'daily',
                    description: this.$t(
                        'Calendar days (e.g., May 1 00:00–23:59).'
                    )
                },
                weekly: {
                    label: this.$t('Weekly'),
                    value: 'weekly',
                    description: this.$t(
                        'Calendar weeks (Mon 00:00–Sun 23:59).'
                    )
                },
                monthly: {
                    label: this.$t('Monthly'),
                    value: 'monthly',
                    description: this.$t(
                        'Full calendar months (1st to last day).'
                    )
                }
            };
            // Trends report resolution option
            const trendsOptions = {
                sixtyMinute: {
                    label: this.$t('60 Minutes'),
                    value: '60-minute',
                    description: this.$t(
                        'Rolling 60-minute periods from the report start time.'
                    )
                },
                twentyFourHour: {
                    label: this.$t('24 Hours'),
                    value: '24-hour',
                    description: this.$t(
                        'From report start time (e.g., May 1 08:00 → May 2 08:00).'
                    )
                },
                sevenDay: {
                    label: this.$t('7 Days'),
                    value: '7-day',
                    description: this.$t(
                        'Any 7-day block from selected start (e.g., Wed–Tue).'
                    )
                }
            };

            if (this.currentReportType === 'trends') {
                return { ...baseOptions, ...trendsOptions };
            }

            return baseOptions;
        }
    },
    methods: {
        ...mapActions(useReportsStore, [
            'setDateRange',
            'setResolution',
            'setIsLoadingReport'
        ]),
        ...mapActions(useFacilitiesStore, {
            loadFacilityGroupsAction: 'loadFacilityGroupsAction'
        }),
        ...mapActions(useAssetsStore, {
            loadAssetsAction: 'loadAssetsAction'
        }),
        /**
         * Set the selected assets to all assets (default)
         */
        resetSelectedAssets() {
            this.assetSelectOptions = [this.DEFAULT_ALL_ASSETS_OPTION];
            this.selectedAsset = null;
        },
        /**
         * Unload unselected reports
         */
        unloadUnselectedReports() {
            Object.keys(this.records).forEach(report => {
                if (report !== this.currentReportType) {
                    this.records[report] = null;
                }
            });
        },
        /**
         * Initialize date range.
         */
        initDateRange() {
            // check if date range has been set from the store
            if (this.getDateRange.start && this.getDateRange.end) {
                // if so, use date range from store
                this.dateTimePicker.start = this.getDateRange.start;
                this.dateTimePicker.end = this.getDateRange.end;
            } else {
                // otherwise, initialize date range from the profile Report default start time
                const reportTime =
                    this.getUser['report_default_start_time'] || '00:00:00';
                const reportDefaultStartTime = reportTime
                    .split(':')
                    .map(x => +x);
                const hour = reportDefaultStartTime[0];
                const minute = reportDefaultStartTime[1];
                const seconds = reportDefaultStartTime[2];

                this.dateTimePicker = {
                    start: new Date(
                        new Date().setHours(hour, minute, seconds) -
                            1000 * 60 * 60 * 24
                    ).toISOString(),
                    end: new Date(
                        new Date().setHours(hour, minute, seconds)
                    ).toISOString()
                };
                this.setDateRange({
                    start: this.dateTimePicker.start,
                    end: this.dateTimePicker.end
                });
            }
        },
        /**
         * Set date range for comparison report depending on selected comparison period
         */
        setComparisonDateRange() {
            // automatically set to previous period
            if (this.selectedComparePeriod.value === 'previous') {
                // get duration of single report date range
                const difference = this.timeDifference();

                // set comparison date range to match duration of single report date range
                this.dateTimePicker.compareStart = new Date(
                    new Date(this.dateTimePicker.start).getTime() - difference
                ).toISOString();
                this.dateTimePicker.compareEnd = new Date(
                    new Date(this.dateTimePicker.start).getTime()
                ).toISOString();
            }
        },
        /**
         * Update selectable filters.
         * This method is called when the selected facility changes.
         */
        async updateSelectableFilters() {
            if (
                this.selectedFacility !== null &&
                this.selectedFacility !== undefined
            ) {
                this.showReport = false;
                try {
                    this.isLoadingFilters = true;

                    // load facility details from API
                    const assets = await this.loadAssets();
                    const assetGroups = await this.loadAssetGroups();
                    const shifts = await this.loadShifts();

                    // set the selectable assets and shifts based on the loaded facility details
                    this.assetFilterOptions = this.getSelectableAssets(
                        assets,
                        assetGroups
                    );
                    this.shiftFilterOptions = this.getSelectableShifts(shifts);
                    // initialize filters base on the loaded facility details

                    // set asset options with all non-deleted assets
                    this.assetSelectOptions = [
                        this.DEFAULT_ALL_ASSETS_OPTION,
                        ...assets
                            .filter(asset => !asset.is_deleted)
                            .map(asset => {
                                return {
                                    label: asset.name,
                                    value: asset.id
                                };
                            })
                    ];
                    // set all assets as the selected asset for the trends report
                    this.selectedAsset = this.DEFAULT_ALL_ASSETS_OPTION;

                    this.initFilters();
                    this.showRefreshFacilityBanner = false;
                } catch (error) {
                    // there are missing facility details necessary for report generation
                    // show banner to prompt user to refresh facility details
                    this.showRefreshFacilityBanner = true;
                } finally {
                    this.isLoadingFilters = false;
                }
            }
        },
        initFilters() {
            // Set of selected assets id
            this.filteredAssets = new Set();
            this.assetsMap.forEach((value, key) =>
                this.filteredAssets.add(key)
            );
            this.filteredShifts = new Set(
                this.shiftFilterOptions.map(shift => shift.value)
            );

            this.filteredDowntimes = new Set([
                'major',
                'minor',
                'planned',
                'unplanned'
            ]);
            this.filteredLossTypes = new Set(['availability', 'performance']);
            this.filteredGroupBy = 'causes';
        },

        /**
         * Get facilities.
         * @return {Object[]} - Array of facilities.
         */
        getFacilities() {
            const facilities = this.getFacilitiesFromStore;
            const facilitiesList = [];
            facilities.map(facility =>
                facilitiesList.push({
                    label: facility.name,
                    value: parseInt(facility.id)
                })
            );
            return facilitiesList;
        },
        async loadAssets() {
            let assets = this.getAssets(this.facilityId, {
                withDeleted: true
            });
            // retrieve assets from store, if not available, load from api
            if (assets.length === 0) {
                // TODO: See comment in loadAssetGroups() method
                // as this suffers from the same limitation with the getters
                // (it returns empty array for both empty array and not loaded array)
                await this.loadAssetsAction({
                    facilityId: this.facilityId,
                    showLoading: true
                });
                assets = this.getAssets(this.facilityId, {
                    withDeleted: true
                });
            }

            return assets;
        },
        /**
         * Load asset groups.
         * Check if asset groups have been loaded in the store, if not, load from api.
         * @return {Object[]} - Array of asset groups.
         */
        async loadAssetGroups() {
            let assetGroups = this.getAssetGroups(this.selectedFacility.value);

            try {
                // TODO: Check if array is empty is not the best way to check if asset groups have been loaded in the store
                // as it is possible that the asset groups have been loaded but the array is empty, this is a limitation with
                // how the store getters are implemented. (it returns empty array for both empty array and not loaded array)
                // For now, until the store getters are refactored, we will assume that empty array means asset groups have not been loaded
                // and so we will load from the api
                if (assetGroups.length === 0) {
                    const response = await this.$axios.get(
                        `assetgroups/?facility=${this.selectedFacility.value}`
                    );
                    assetGroups = response.data.results;
                }
            } catch (error) {
                console.error('Failed to fetch assets group due to: ', error);
                throw error;
            }

            return assetGroups;
        },
        /**
         * Load shifts.
         * Shifts are not stored in the store, so we need to load from the api every time.
         * @return {Object[]} - Array of shifts.
         */
        async loadShifts() {
            try {
                const response = await this.$axios.get(
                    `facilities/${this.selectedFacility.value}/shifts/`
                );
                return response.data.results;
            } catch (error) {
                console.error('Failed to fetch shifts due to: ', error);
                throw error;
            }
        },
        /**
         * Get selectable asset filter options.
         * @param {Object[]} assets - List of assets.
         * @param {Object[]} assetGroups - List of asset groups.
         * @return {Object[]} - List of asset filter options.
         */
        getSelectableAssets(assets, assetGroups) {
            // reset asset list when changing facility
            this.assetsMap = new Map();
            assets.forEach(a => {
                // if asset is_deleted, append (deleted) to the asset name
                let assetName = a.name;
                if (a.is_deleted) {
                    assetName = this.$t('{assetName} (Deleted)', {
                        assetName: a.name
                    });
                }
                this.assetsMap.set(a.id, assetName);
            });

            // store assets key in set from asset map
            const assetsSet = new Set(this.assetsMap.keys());

            const assetFilterOptions = [];

            assetGroups.map(assetGroup => {
                assetFilterOptions.push({
                    label: assetGroup.name,
                    // Append '_group' to group value to distinguish it from individual asset IDs
                    // This prevents collisions between group IDs and asset IDs in filtering logic
                    value: assetGroup.id + '_group',
                    selectable: false,

                    children: assetGroup.assets.map(assetId => {
                        assetsSet.delete(assetId);
                        return {
                            label: this.assetsMap.get(assetId),
                            value: assetId
                        };
                    })
                });
            });

            // add the rest of the assets to the selectable assets list which are not in any group
            assetsSet.forEach(assetId => {
                assetFilterOptions.push({
                    label: this.assetsMap.get(assetId),
                    value: assetId
                });
            });

            return assetFilterOptions;
        },
        /**
         * Get selectable shift filter options.
         * @param {Object[]} shifts - List of shifts.
         * @return {Object[]} - List of shift filter options.
         */
        getSelectableShifts(shifts) {
            const shiftFilterOptions = [];
            shifts.forEach(shift => {
                shiftFilterOptions.push({
                    label: shift.name,
                    value: shift.id
                });
                this.shiftIdToName[shift.id] = shift.name;
            });

            return shiftFilterOptions;
        },
        /**
         * Handle report submit button.
         */
        async onSubmit() {
            const REPORT_MAX_END_DATE_DELAY = 15; // min
            // check that the start date is before the end date

            // field validation for report interval
            if (this.dateTimePicker.start >= this.dateTimePicker.end) {
                this.$notifyError(
                    this.$t('Report start date must come before the end date.')
                );
                return;
            } else if (
                this.dateTimePicker.end >
                new Date(
                    new Date().getTime() - REPORT_MAX_END_DATE_DELAY * 60000
                ).toISOString() // now-15 mints
            ) {
                this.$notifyError(
                    this.$t('Report end date should be at most 15 min ago')
                );
                return;
            } else if (this.isBenchmarkReport) {
                const validationMessage = this.benchmarkEndPeriodValidator();
                if (validationMessage !== true) {
                    this.$notifyError(validationMessage);
                    return;
                }
            }

            // field validation for loss report date range (maximum months limit)
            if (this.currentReportType === 'losses') {
                const lossValidationMessage =
                    this.lossReportDateRangeValidator();
                if (lossValidationMessage !== true) {
                    this.$notifyError(lossValidationMessage);
                    return;
                }
            }

            // If asset select is shown and no asset is selected, show error message
            if (this.showAssetSelect && this.selectedAsset === null) {
                this.$notifyError(
                    this.$t(
                        'You must select an asset before generating the report.'
                    )
                );
                return;
            }

            // field validation when comparing reports
            if (this.isComparing) {
                if (
                    this.dateTimePicker.compareStart >
                    this.dateTimePicker.compareEnd
                ) {
                    this.$notifyError(
                        this.$t(
                            'Comparison start date must come before end date.'
                        )
                    );
                    return;
                }
                // check that comparison period comes before current period
                else if (
                    this.dateTimePicker.compareEnd >
                        this.dateTimePicker.start ||
                    this.dateTimePicker.end < this.dateTimePicker.compareStart
                ) {
                    this.$notifyError(
                        this.$t(
                            'Comparison period must occur before the current period.'
                        )
                    );
                    return;
                }
            }

            // update date range set in store
            this.setDateRange({
                start: this.dateTimePicker.start,
                end: this.dateTimePicker.end,
                compareStart: this.dateTimePicker.compareStart,
                compareEnd: this.dateTimePicker.compareEnd
            });

            this.setResolution({
                resolution: this.selectedResolution.value
            });
            this.isLoading = true;

            // unload previously loaded report
            this.records[this.currentReportType] = null;

            // generate the report.
            this.generateReportAsync();
        },
        /**
         * Get ordered resolution options based on report type and date range.
         * @param {string} rangeCategory - The date range category ('short', 'medium', 'long')
         * @return {Array} - Ordered array of resolution options
         */
        getOrderedResolutions(rangeCategory) {
            const resolutionTypes = this.createResolutionOptions;

            if (this.currentReportType === 'trends') {
                if (rangeCategory === 'short') {
                    return [
                        resolutionTypes.hourly,
                        resolutionTypes.sixtyMinute,
                        resolutionTypes.daily,
                        resolutionTypes.twentyFourHour
                    ];
                } else if (rangeCategory === 'medium') {
                    return [
                        resolutionTypes.hourly,
                        resolutionTypes.sixtyMinute,
                        resolutionTypes.daily,
                        resolutionTypes.twentyFourHour,
                        resolutionTypes.weekly,
                        resolutionTypes.sevenDay
                    ];
                } else {
                    // long range
                    return [
                        resolutionTypes.daily,
                        resolutionTypes.twentyFourHour,
                        resolutionTypes.weekly,
                        resolutionTypes.sevenDay,
                        resolutionTypes.monthly
                    ];
                }
            } else {
                // Standard report types (non-trends)
                if (rangeCategory === 'short') {
                    return [resolutionTypes.hourly, resolutionTypes.daily];
                } else if (rangeCategory === 'medium') {
                    return [
                        resolutionTypes.hourly,
                        resolutionTypes.daily,
                        resolutionTypes.weekly
                    ];
                } else {
                    // long range
                    return [
                        resolutionTypes.daily,
                        resolutionTypes.weekly,
                        resolutionTypes.monthly
                    ];
                }
            }
        },
        /**
         * Updates the selectable resolution options based on the report date range.
         *
         * Business rules:
         * - ≤ 2 days: Hourly (default) and Daily options
         * - 3-28 days: Hourly, Daily (default), and Weekly options
         * - > 28 days: Daily, Weekly (default), and Monthly options
         *
         * Performance: Caches day difference calculation and creates option objects efficiently.
         * Security: Validates date picker values before processing.
         */
        toggleSelectableResolutions() {
            // Input validation
            if (!this.dateTimePicker?.start || !this.dateTimePicker?.end) {
                console.warn('Invalid date range for resolution calculation');
                return;
            }

            // Cache expensive calculation
            const daysDiff = this.daysDifference();

            // Define resolution configuration with business rules
            const RESOLUTION_THRESHOLDS = {
                SHORT_RANGE: 2, // ≤ 2 days
                MEDIUM_RANGE: 28 // 3-28 days, > 28 days for long range
            };

            // Pre-create resolution option objects to avoid duplication
            const resolutionTypes = this.createResolutionOptions;

            // Determine date range category and default resolution
            let rangeCategory, defaultResolution;
            if (daysDiff <= RESOLUTION_THRESHOLDS.SHORT_RANGE) {
                rangeCategory = 'short';
                defaultResolution = resolutionTypes.hourly;
            } else if (daysDiff <= RESOLUTION_THRESHOLDS.MEDIUM_RANGE) {
                rangeCategory = 'medium';
                defaultResolution = resolutionTypes.daily;
            } else {
                rangeCategory = 'long';
                defaultResolution = resolutionTypes.weekly;
            }

            // Get ordered resolutions based on report type and range
            const availableResolutions =
                this.getOrderedResolutions(rangeCategory);

            // Update component state
            this.resolutionOptions = availableResolutions;
            this.selectedResolution = defaultResolution;
        },
        /**
         * Convert UTC date time to timestamp
         */
        getTimeStamp(dateTime) {
            return Math.floor(new Date(dateTime).getTime() / 1000);
        },
        /**
         * Get the payload for the API request for report
         * and get the report
         */
        generateReportAsync() {
            this.resetLoadingState();

            const payload = {
                intervals: [
                    {
                        start: this.getTimeStamp(this.dateTimePicker.start),
                        end: this.getTimeStamp(this.dateTimePicker.end)
                    }
                ],
                timezone: this.localTimezone,
                report: this.currentReportType
            };

            if (this.isBenchmarkReport) {
                payload['facilities'] =
                    this.facilitiesIdsInSelectedFacilityGroup;
            } else {
                payload['facility'] = this.facilityId;
            }

            if (
                this.currentReportType === 'data_entry_performance' ||
                this.currentReportType === 'trends'
            ) {
                payload['resolution'] = this.selectedResolution.value;
            }

            // Only trends report will have pre-generation asset filtering
            if (this.currentReportType === 'trends') {
                // Include an asset filter in the payload if the user has a selected asset
                if (this.selectedAsset && this.selectedAsset.value !== 'all') {
                    payload['assets'] = [this.selectedAsset.value];
                }
            }

            if (this.isComparing) {
                // The trends and changeover reports do not support comparison
                if (
                    this.currentReportType !== 'trends' &&
                    this.currentReportType !== 'changeover'
                ) {
                    payload['intervals'].push({
                        start: this.getTimeStamp(
                            this.dateTimePicker.compareStart
                        ),
                        end: this.getTimeStamp(this.dateTimePicker.compareEnd)
                    });
                }
            }
            // generate the report
            this.startAsyncReportGeneration(payload);

            // For loss reports, also fetch speed losses in parallel
            if (
                this.currentReportType === 'losses' &&
                isFeatureEnabled('SPEED_LOSSES_IN_LOSSES_REPORT')
            ) {
                this.fetchSpeedLosses(payload);
            }
        },
        /**
         * Fetch speed losses for the loss report in parallel
         * @param {Object} payload - The report payload containing intervals and facility
         */
        async fetchSpeedLosses(payload) {
            this.isLoadingSpeedLosses = true;
            this.additionalMetrics.speed_losses = null;

            try {
                const speedLossPayload = {
                    intervals: payload.intervals,
                    timezone: payload.timezone,
                    facility: payload.facility,
                    metric: 'speed_losses'
                };

                const results = await this.speedLossAsyncManager.asyncLoad(
                    'facilities/metrics/calculate/',
                    speedLossPayload
                );

                // Process speed loss results
                this.processSpeedLossResults(results);
            } catch (error) {
                // Speed loss fetch failed, but we don't want to block the main report
                console.error('Failed to fetch speed losses:', error);
                this.additionalMetrics.speed_losses = null;
            } finally {
                this.isLoadingSpeedLosses = false;
            }
        },
        /**
         * Process speed loss API results using whichReportMode to identify report periods
         * @param {Array} results - Speed loss results from the API
         */
        processSpeedLossResults(results) {
            // Reset speed_losses structure
            this.additionalMetrics.speed_losses = {};
            this.additionalMetrics.speed_losses = shallowReactive({
                currentReportData: []
            });

            if (this.isComparing) {
                this.additionalMetrics.speed_losses.comparisonReportData = [];
            }

            // Use whichReportMode to correctly assign results to current/comparison periods
            results.forEach(result => {
                const reportMode = this.whichReportMode(result);
                if (reportMode === this.reportModes.CURRENTREPORT) {
                    this.additionalMetrics.speed_losses.currentReportData =
                        result.records || [];
                } else if (
                    this.isComparing &&
                    reportMode === this.reportModes.COMPARISONREPORT
                ) {
                    this.additionalMetrics.speed_losses.comparisonReportData =
                        result.records || [];
                }
            });
        },
        /**
         * Send API request to generate report asynchronously via celery task
         * @param (Object) payload - http payload
         */
        async startAsyncReportGeneration(payload) {
            // enable the loading state.
            this.isLoading = true;
            // clear previous report
            this.showReport = false;
            try {
                // get start time
                const start = new Date();
                // generate report
                const result = await this.asyncManager.asyncLoad(
                    'facilities/reports/generate/',
                    payload
                );
                this.processAPIResults(result);
                this.isLoading = false;
                this.showReport = true;
                // calculate total time of click-to-screen for report loading
                const end = new Date();

                // calculate report period days
                const report_period_days = payload['intervals'][0]
                    ? Number(
                          (
                              (payload['intervals'][0]['end'] -
                                  payload['intervals'][0]['start']) /
                              (60 * 60 * 24)
                          ).toFixed(3) // converted to days
                      )
                    : null;
                const comparison_period_days = payload['intervals'][1]
                    ? Number(
                          (
                              (payload['intervals'][1]['end'] -
                                  payload['intervals'][1]['start']) /
                              (60 * 60 * 24)
                          ).toFixed(3) // converted to days
                      )
                    : null;
                const total_report_period_days =
                    report_period_days +
                    (comparison_period_days
                        ? Number(comparison_period_days)
                        : 0);
                // find facility/facilities
                const facility = payload['facility']
                    ? {
                          name: payload['facility']
                              ? this.getFacility(payload['facility']).name
                              : null,
                          id: payload['facility']
                              ? this.getFacility(payload['facility']).id
                              : null
                      }
                    : null;
                const facilities = payload['facilities']
                    ? payload['facilities'].map(id => {
                          const facilityObj = this.getFacility(id);
                          return {
                              name: facilityObj ? facilityObj.name : null,
                              id: id
                          };
                      })
                    : null;

                // send the event to posthog
                posthog.capture('report_loading', {
                    report: payload['report'],
                    loading_time_sec: (end - start) / 1000, // seconds
                    tenant: this.getTenant || null,
                    facility: facility,
                    facilities: facilities,
                    comparison_report: payload['intervals'][1] ? true : false,
                    report_period_days: report_period_days,
                    comparison_period_days: comparison_period_days,
                    total_report_period_days: total_report_period_days
                });
            } catch (error) {
                this.asyncReportFailed();
            }
        },
        /**
         * Assign the report data to be displayed
         * @param (Object) report - report object
         */
        processAPIResults(results) {
            // reset the current report type
            this.records[this.currentReportType] = {};
            // assign the report object
            try {
                // there is a possibility that facility does not have any
                // data during the selected period, so the backend API will
                // just sends empty results.
                this.records[this.currentReportType] = shallowReactive({
                    currentReportData: []
                });
                if (this.isComparing) {
                    this.records[this.currentReportType][
                        'comparisonReportData'
                    ] = [];
                }

                // Handle benchmark report data processing
                if (this.isBenchmarkReport) {
                    const [benchmarkData = []] = results;

                    if (
                        Array.isArray(benchmarkData) &&
                        benchmarkData.length > 0
                    ) {
                        this.records[this.currentReportType][
                            'currentReportData'
                        ] = shallowReactive(benchmarkData);
                    }
                    return;
                }

                // Handle regular report data processing
                results.forEach(report => {
                    // NOTE: There is possibility that one of the
                    // current or comparison period selected does not
                    // have data and the API would just send one result/report
                    // so we have to make sure which report is present at the first
                    // index.
                    const reportMode = this.whichReportMode(report);
                    if (reportMode === this.reportModes.CURRENTREPORT) {
                        this.records[this.currentReportType][
                            'currentReportData'
                        ] = shallowReactive(report.report);
                    } else if (
                        this.isComparing &&
                        reportMode === this.reportModes.COMPARISONREPORT
                    ) {
                        this.records[this.currentReportType][
                            'comparisonReportData'
                        ] = shallowReactive(report.report);
                    }
                });
            } catch (error) {
                console.log(error);
                throw new Error(error);
            }
        },
        /**
         * Helper method to determine the appropriate report type for the given data.
         *
         * NOTE: Report response schema from backend API is not
         * very helpful in recognizing data belongs to which
         * type of report i.e currentReport or comparisonReport.
         * @param report{Object} report object received as is from backend API
         */
        whichReportMode(report) {
            const reportStart = new Date(report.start).toISOString();
            const reportEnd = new Date(report.end).toISOString();
            if (
                reportStart === this.dateTimePicker.start &&
                reportEnd == this.dateTimePicker.end
            ) {
                return this.reportModes.CURRENTREPORT;
            }
            if (
                reportStart === this.dateTimePicker.compareStart &&
                reportEnd == this.dateTimePicker.compareEnd
            ) {
                return this.reportModes.COMPARISONREPORT;
            }
        },
        /**
         * Async report generation failed
         * @param (String) error - Error message
         */
        asyncReportFailed(error = '') {
            this.$notifyError(
                this.$t(
                    'Unable to generate the report at this time. Please try again later.'
                ),
                error
            );
            this.resetLoadingState();
        },
        /**
         * Calculate number of days between the start date
         * and end date selected for the report.
         */
        daysDifference() {
            return Math.ceil(
                Math.abs(
                    new Date(this.dateTimePicker.start) -
                        new Date(this.dateTimePicker.end)
                ) /
                    (1000 * 60 * 60 * 24)
            );
        },
        /**
         * Calculate the exact difference between the start date
         * and end date selected for the comparison period.
         */
        timeDifference() {
            return Math.abs(
                new Date(this.dateTimePicker.end) -
                    new Date(this.dateTimePicker.start)
            );
        },
        /**
         * Stop all activities (frontend and backend) related to
         * the async report generation
         */
        resetLoadingState() {
            this.setIsLoadingReport(false);
            this.isLoading = false;
            this.isLoadingSpeedLosses = false;
            this.additionalMetrics.speed_losses = null;
            this.showReport = false;
            this.records[this.currentReportType] = null;
        },
        /**
         * Cancel button for the asynchronous report generation
         */
        onCancel() {
            this.asyncManager.cancelAsyncLoading({});
            this.speedLossAsyncManager.cancelAsyncLoading({});
            this.resetLoadingState();
        },
        /**
         * Cancel button for the asynchronous speed-loss-calculation
         */
        onCancelSpeedLoss() {
            this.speedLossAsyncManager.cancelAsyncLoading({});
            this.isLoadingSpeedLosses = false;
        },
        /**
         * Remove the asset from the filtered assets
         * * @param (Number) assetId - ID of the asset
         */
        removeAssetFilter(assetId) {
            this.filteredAssets.delete(assetId);

            this.filteredAssets = new Set(this.filteredAssets);
        },
        /**
         * Remove the loss from the filtered losses
         * * @param (Number) lossId - ID of the filtered Loss
         */
        removeLossTypeFilter(lossId) {
            this.filteredLossTypes.delete(lossId);

            this.filteredLossTypes = new Set(this.filteredLossTypes);
        },
        /**
         * Remove the downtime from the filtered downtimes
         * * @param (Number) downtimeId - ID of the filtered downtime
         */
        removeDowntimeFilter(downtimeId) {
            this.filteredDowntimes.delete(downtimeId);

            this.filteredDowntimes = new Set(this.filteredDowntimes);
        },
        /**
         * Remove the shift from the filtered shifts
         * * @param (Number) shiftId - ID of the filtered shift
         */
        removeShiftFilter(shiftId) {
            this.filteredShifts.delete(shiftId);

            this.filteredShifts = new Set(this.filteredShifts);
        },

        /**
         * Return a string capitalized
         * * @param (String)
         */
        capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
        /**
         * Validate the end period of the benchmark report.
         * The end date must be at least one hour before the current time.
         * @return {String | Boolean} Error message or true if validation passes.
         */
        benchmarkEndPeriodValidator() {
            if (!this.isBenchmarkReport) {
                return true;
            }
            const end = this.dateTimePicker.end;
            const current = new Date();
            const oneHourAgo = new Date(current.getTime() - 1000 * 60 * 60);
            if (new Date(end) > oneHourAgo) {
                return this.$t(
                    'The benchmark report end date must be at least one hour before the current time.'
                );
            }
            return true;
        },
        /**
         * Validation for loss report date range to ensure it doesn't exceed the maximum months limit.
         * @return {String | Boolean} Error message or true if validation passes.
         */
        lossReportDateRangeValidator() {
            if (this.currentReportType !== 'losses') {
                return true;
            }

            const start = new Date(this.dateTimePicker.start);
            const end = new Date(this.dateTimePicker.end);

            // Calculate maximum months from the start date
            const maxMonthsFromStart = new Date(start);
            maxMonthsFromStart.setMonth(
                maxMonthsFromStart.getMonth() + LOSS_REPORT_MAX_MONTHS
            );

            if (end > maxMonthsFromStart) {
                return this.$t(
                    `Loss reports are limited to a maximum date range of {LOSS_REPORT_MAX_MONTHS} months. If you need a report covering a longer period, generate multiple reports for consecutive ranges (e.g., Jan–Mar, Apr–Jun) and combine them as needed.`,
                    { LOSS_REPORT_MAX_MONTHS }
                );
            }

            return true;
        },
        /**
         * Load facility groups.
         * @return {Promise} A promise that resolves with the response or rejects with an error.
         */
        async loadFacilityGroups() {
            try {
                await this.loadFacilityGroupsAction();
            } catch (error) {
                console.error('Failed to load facility groups due to: ', error);
                this.$notifyError(
                    this.$t(
                        'Failed to load facility groups. Please try again later.'
                    )
                );
            }
        },
        /**
         * Preselect facility if only one facility or facility group is available
         */
        preselectFacilityIfSingle() {
            // For non-benchmark reports, preselect facility if only one facility is available
            if (!this.isBenchmarkReport && this.facilities.length === 1) {
                this.selectedFacility = this.facilities[0];
            }
            // For benchmark reports, preselect the only facility or facility group available
            else if (this.isBenchmarkReport && this.isSingleFacilityOrGroup) {
                // Assign the only facility or facility group found.
                // This will be the first option in the facilityAndFacilityGroupsOptions array that is not a separator
                this.selectedFacilityGroup =
                    this.facilityAndFacilityGroupsOptions.find(
                        option => !option.separator
                    );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$table-height: 100%;
$panel-border: 1px solid $secondary;

.filter-panel {
    margin: 8px 4px;
    padding: 10px 20px !important;
    width: auto;
}
.filter-panel.q-btn--outline:before {
    border: 1px solid $accent;
}
.filter-panel-label {
    color: black !important;
}
.filter-panel-tablet {
    width: 48% !important;
    margin: 4px;
}
.filter-panel-mobile {
    width: 98% !important;
    margin: 4px auto;
}
.filter-row {
    width: 100%;
}
.pad-facility {
    padding-bottom: 20px;
}

:deep(.table) {
    border: $panel-border;
}

:deep(.table__header) {
    color: white;
    font-size: 14px;
    font-weight: 700;
}

/* Override top panel table style */
:deep(.q-table__container > div:first-child) {
    border-top-left-radius: unset;
    border-top-right-radius: unset;
}

:deep(.assets > .q-field__inner > .q-field__control) {
    min-height: 93px;
}

:deep(
    .groupBy
        .q-field
        .q-field__control
        .q-field__control-container
        .q-field__native
) {
    color: $primary;
}

.groupByText {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.7rem;
    grid-column: 1;
    grid-row: 1;
}

.groupByOptions {
    grid-column: 1;
    grid-row: 1;
    padding-top: 8px;
}

.groupByContainer {
    display: grid;
    padding-right: 25px;
}

.downloadButtonBackground {
    background-color: $primary;
}

:deep(.filter-row .filter-panel .q-btn__wrapper::before) {
    border: 0.7px solid $accent !important;
}

:deep(.filter-panel .block),
:deep(.applied-filters-panel .block) {
    font-size: 14px !important;
}

:deep(.filter-panel .q-icon) {
    font-size: 1.3em;
}
</style>
